import * as React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  alpha,
} from '@mui/material';
import {
  FaceRetouchingNatural as FaceIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { faceAuthService, FaceAuthStatus } from '../services/faceAuthService';
import FaceCaptureDialog from './FaceCaptureDialog';
import { useNotification } from '../crud-dashboard/components/CustomNotification';
import { useAuth } from '../contexts/AuthContext';

export default function FaceIdSettings() {
  const { t } = useLanguage();
  const notifications = useNotification();
  const { refreshFaceAuthStatus } = useAuth();

  const [status, setStatus] = React.useState<FaceAuthStatus | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [showCaptureDialog, setShowCaptureDialog] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Load face auth status on mount
  React.useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await faceAuthService.getStatus();
      if (response.success && response.data) {
        setStatus(response.data);
      } else {
        setError('Failed to load face authentication status');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    setShowCaptureDialog(true);
  };

  const handleCapture = async (imageBase64: string) => {
    setIsProcessing(true);
    setError(null);

    try {
      const response = await faceAuthService.registerFace(imageBase64);

      if (response.success) {
        notifications.show(
          t('faceAuth.registerSuccess') || 'Face registered successfully',
          { severity: 'success', autoHideDuration: 3000 }
        );
        await loadStatus();
        await refreshFaceAuthStatus();
      } else {
        notifications.show(
          response.message || 'Failed to register face',
          { severity: 'error', autoHideDuration: 5000 }
        );
      }
    } catch (err: any) {
      notifications.show(
        err.message || 'Failed to register face',
        { severity: 'error', autoHideDuration: 5000 }
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteFace = async () => {
    if (!window.confirm(t('faceAuth.confirmDelete') || 'Are you sure you want to delete your Face ID?')) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const response = await faceAuthService.deleteFace();

      if (response.success) {
        notifications.show(
          t('faceAuth.deleteSuccess') || 'Face ID deleted successfully',
          { severity: 'success', autoHideDuration: 3000 }
        );
        await loadStatus();
        await refreshFaceAuthStatus();
      } else {
        notifications.show(
          response.message || 'Failed to delete face',
          { severity: 'error', autoHideDuration: 5000 }
        );
      }
    } catch (err: any) {
      notifications.show(
        err.message || 'Failed to delete face',
        { severity: 'error', autoHideDuration: 5000 }
      );
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#028bcb' }} />
      </Box>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgba(2, 139, 203, 0.08) 0%, rgba(2, 139, 203, 0.03) 100%)',
          border: '2px solid',
          borderColor: alpha('#028bcb', 0.2),
          borderRadius: 3,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: '#028bcb',
            boxShadow: `0 12px 40px ${alpha('#028bcb', 0.2)}`,
            transform: 'translateY(-4px)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #028bcb 0%, #0277a8 50%, #028bcb 100%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 3s linear infinite',
          },
          '@keyframes shimmer': {
            '0%': { backgroundPosition: '200% 0' },
            '100%': { backgroundPosition: '-200% 0' },
          },
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            p: 4,
            pb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${alpha('#028bcb', 0.15)} 0%, ${alpha('#028bcb', 0.05)} 100%)`,
              border: '2px solid',
              borderColor: alpha('#028bcb', 0.3),
              transition: 'all 0.3s ease',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: -8,
                borderRadius: '50%',
                background: `conic-gradient(from 0deg, transparent, ${alpha('#028bcb', 0.2)}, transparent)`,
                animation: 'rotate 4s linear infinite',
              },
              '@keyframes rotate': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          >
            <FaceIcon
              sx={{
                fontSize: 42,
                color: '#028bcb',
                position: 'relative',
                zIndex: 1,
                filter: 'drop-shadow(0 2px 8px rgba(2, 139, 203, 0.3))',
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 0.5,
                letterSpacing: '-0.5px',
              }}
            >
              {t('faceAuth.title') || 'Face ID Authentication'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
              }}
            >
              {t('faceAuth.description') || 'Use your face to securely login to your account'}
            </Typography>
          </Box>
        </Box>

        {/* Status Section */}
        <Box
          sx={{
            px: 4,
            pb: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2.5,
              borderRadius: 2,
              background: status?.has_face_registered
                ? `linear-gradient(135deg, ${alpha('#4caf50', 0.08)} 0%, ${alpha('#4caf50', 0.03)} 100%)`
                : `linear-gradient(135deg, ${alpha('#ff9800', 0.08)} 0%, ${alpha('#ff9800', 0.03)} 100%)`,
              border: '1px solid',
              borderColor: status?.has_face_registered ? alpha('#4caf50', 0.2) : alpha('#ff9800', 0.2),
              transition: 'all 0.3s ease',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: status?.has_face_registered
                  ? alpha('#4caf50', 0.15)
                  : alpha('#ff9800', 0.15),
              }}
            >
              {status?.has_face_registered ? (
                <CheckCircleIcon sx={{ fontSize: 28, color: '#4caf50' }} />
              ) : (
                <CancelIcon sx={{ fontSize: 28, color: '#ff9800' }} />
              )}
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: status?.has_face_registered ? '#4caf50' : '#ff9800',
                  mb: 0.5,
                }}
              >
                {status?.has_face_registered
                  ? t('faceAuth.registered') || 'Face ID Registered'
                  : t('faceAuth.notRegistered') || 'Face ID Not Registered'}
              </Typography>
              {status?.has_face_registered && status.face_registered_at && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {new Date(status.face_registered_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Actions Section */}
        <Box sx={{ px: 4, pb: 4 }}>
          {!status?.has_face_registered ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              disabled={isProcessing}
              fullWidth
              sx={{
                py: 2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #028bcb 0%, #0277a8 100%)',
                boxShadow: `0 4px 16px ${alpha('#028bcb', 0.3)}`,
                border: 'none',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0277a8 0%, #026a94 100%)',
                  boxShadow: `0 6px 24px ${alpha('#028bcb', 0.4)}`,
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  background: alpha('#028bcb', 0.3),
                },
              }}
            >
              {isProcessing ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                t('faceAuth.registerFace') || 'Register Face ID'
              )}
            </Button>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Button
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteFace}
                disabled={isProcessing}
                fullWidth
                sx={{
                  py: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  color: '#f44336',
                  borderColor: alpha('#f44336', 0.3),
                  borderWidth: 2,
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#f44336',
                    borderWidth: 2,
                    background: alpha('#f44336', 0.05),
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 16px ${alpha('#f44336', 0.2)}`,
                  },
                  '&:disabled': {
                    borderColor: alpha('#f44336', 0.2),
                    color: alpha('#f44336', 0.4),
                  },
                }}
              >
                {isProcessing ? (
                  <CircularProgress size={24} sx={{ color: '#f44336' }} />
                ) : (
                  t('faceAuth.deleteFace') || 'Delete Face ID'
                )}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Face Capture Dialog */}
      <FaceCaptureDialog
        open={showCaptureDialog}
        onClose={() => setShowCaptureDialog(false)}
        onCapture={handleCapture}
        title={t('faceAuth.captureTitle') || 'Register Your Face'}
        description={
          t('faceAuth.captureDescription') ||
          'Position your face within the circle. Make sure you are in a well-lit area and looking directly at the camera.'
        }
      />
    </>
  );
}

